import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { computeVariance } from '../lib/variance.js'

// Convert a YYYY-MM-DD date string (intended to mean midnight of that
// date in the given timezone) into a UTC ISO string suitable for
// querying timestamptz columns.
//
// Example: localMidnightToUtcIso('2026-04-28', 'America/Chicago')
//   during CDT returns '2026-04-28T05:00:00.000Z' (midnight Chicago
//   = 5 AM UTC). During CST (winter) it would return '2026-04-28T06:00:00.000Z'.
//
// Why not just append 'Z': a naive-local string like '2026-04-28T00:00:00'
// is interpreted as UTC by Postgres, which silently shifts the
// variance window by the restaurant's UTC offset (5–8 hours for
// Chicago). For a 7-day total the symmetric shift mostly cancels
// out, but per-day breakdowns and edge-of-window logs end up in
// the wrong bucket. This helper does the math correctly.
function localMidnightToUtcIso(dateStr, timeZone, hour = 0, minute = 0, second = 0) {
  if (!timeZone) {
    // No timezone available — fall back to browser-local interpretation.
    return new Date(`${dateStr}T${pad(hour)}:${pad(minute)}:${pad(second)}`).toISOString()
  }
  // Trick: format an already-known UTC instant in the target timezone,
  // see how it shifts. The difference is the offset we need to apply.
  const utcMidnight = new Date(`${dateStr}T00:00:00Z`)
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year:   'numeric',
    month:  '2-digit',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = {}
  for (const p of fmt.formatToParts(utcMidnight)) parts[p.type] = p.value
  // What does the timezone read when UTC says midnight?
  // For Chicago CDT: parts = { year:2026, month:04, day:27, hour:19, ... }
  // (because 00:00 UTC is 19:00 the previous day in Chicago)
  const tzReadingOfUtcMidnight = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    parts.hour === '24' ? 0 : Number(parts.hour),
    Number(parts.minute), Number(parts.second),
  )
  const offsetMs = utcMidnight.getTime() - tzReadingOfUtcMidnight
  // Now compute the desired moment: midnight (or hour/min/sec) on dateStr in timeZone.
  // = utcMidnight + offsetMs + (hour/min/sec offset within the day)
  const targetMs = utcMidnight.getTime()
                 + offsetMs
                 + (hour * 3600 + minute * 60 + second) * 1000
  return new Date(targetMs).toISOString()
}

function pad(n) { return String(n).padStart(2, '0') }

// Drives the Variance screen — Theoretical-vs-Actual inventory usage
// for a manager-chosen date range.
//
// Fetches the full set of inputs computeVariance needs:
//   1. items (with pack info + current_on_hand)
//   2. recipes + recipe_components + nested item joins
//   3. item_sales whose period falls within the variance window
//   4. on_hand_snapshots with source='baseline' at-or-before the
//      window start (the variance starting line)
//   5. receive_logs.logged_at within the window
//   6. waste_logs.logged_at within the window
//
// Then calls computeVariance to fold all that into per-item variance
// rows. Re-fetches whenever the date range changes; keeps the
// previous result while loading so the table doesn't flash blank.
//
// The hook is parameterless on purpose — period state lives on the
// screen so the manager can pick a range without remounting the hook.
// Pass period_start + period_end via setPeriod().
export function useVarianceData() {
  const [period, setPeriod] = useState(() => defaultLast7())
  const [result, setResult]   = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  const fetchVariance = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { period_start, period_end } = period

      // Get the restaurant timezone so the date filter is correct.
      // Without this, "2026-04-28" gets treated as UTC and a Chicago
      // restaurant's variance window shifts by ~5-6 hours. Per-day
      // breakdowns end up bucketing logs into the wrong day.
      const { data: rest } = await supabase
        .from('restaurants')
        .select('timezone')
        .eq('active', true)
        .order('created_at')
        .limit(1)
        .maybeSingle()
      const tz = rest?.timezone || null

      // Build a window for logs. logged_at is timestamptz; we want
      // [period_start 00:00, period_end 23:59:59] interpreted IN the
      // restaurant's timezone, then converted to UTC for the query.
      const startTs = localMidnightToUtcIso(period_start, tz, 0,  0,  0)
      const endTs   = localMidnightToUtcIso(period_end,   tz, 23, 59, 59)

      const [
        itemsRes,
        recipesRes,
        salesRes,
        snapshotsRes,
        receivesRes,
        wastesRes,
      ] = await Promise.all([
        supabase
          .from('items')
          .select(`
            id, name, unit, cost_per_unit, current_on_hand,
            pack_size, pack_unit, active
          `)
          .order('name'),
        supabase
          .from('recipes')
          .select(`
            id, name, yield_servings, item_id,
            components:recipe_components(
              id, item_id, quantity, unit,
              item:item_id(id, name, unit, cost_per_unit, pack_size, pack_unit)
            )
          `),
        supabase
          .from('item_sales')
          .select('item_id, item_name, period_start, period_end, quantity_sold')
          // include any row whose period overlaps the variance window:
          // period_end >= start AND period_start <= end
          .gte('period_end',   period_start)
          .lte('period_start', period_end),
        supabase
          .from('on_hand_snapshots')
          .select('item_id, snapshot_at, quantity, source')
          .eq('source', 'baseline')
          .order('snapshot_at', { ascending: false }),
        supabase
          .from('receive_logs')
          .select('item_id, quantity, unit, logged_at')
          .gte('logged_at', startTs)
          .lte('logged_at', endTs),
        supabase
          .from('waste_logs')
          .select('item_id, quantity, unit, logged_at')
          .gte('logged_at', startTs)
          .lte('logged_at', endTs),
      ])

      const errs = [
        itemsRes.error, recipesRes.error, salesRes.error,
        snapshotsRes.error, receivesRes.error, wastesRes.error,
      ].filter(Boolean)
      if (errs.length) throw errs[0]

      const computed = computeVariance({
        period_start,
        period_end,
        items:             itemsRes.data ?? [],
        recipes:           recipesRes.data ?? [],
        item_sales:        salesRes.data ?? [],
        on_hand_snapshots: snapshotsRes.data ?? [],
        receive_logs:      receivesRes.data ?? [],
        waste_logs:        wastesRes.data ?? [],
      })
      setResult(computed)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useVarianceData]', e)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => { fetchVariance() }, [fetchVariance])

  return {
    result,
    loading,
    error,
    period,
    setPeriod,
    refresh: fetchVariance,
  }
}

// Default window: last 7 days inclusive, in browser local TZ.
function defaultLast7() {
  const today = new Date()
  const start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
  return {
    period_start: ymd(start),
    period_end:   ymd(today),
  }
}

function ymd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
